import { action, makeAutoObservable, observable } from "mobx";

export type QFastTimerange =
    | "today"
    | "yesterday"
    | "last_7_days"
    | "last_30_days"
    | "last_3_months"
    | "last_year"
    | "year_to_date"
    | "all"
    | "custom";

export type QFastTotal = {
    interviews: number;
    voters: number;
    famcode: number;
};

export type QFastBoothWiseRow = { part: string; interviews: number; voters: number };

export type QFastResults = {
    aggs: Record<string, Array<{ key: string | number; count: number }>>;
    summary: Record<string, number>;
    timeline: Array<{ date: string; count: number; enumerators?: number }>;
    surveyors: Array<{ id: string | number; count: number; user?: Record<string, unknown> }>;
    booth_wise?: QFastBoothWiseRow[];
};

/** From project qfast_dashboard.voter_enrich */
export type QFastVoterEnrichSimple = [string, string];
export type QFastVoterEnrichNested = [string, string, boolean, Record<string, string>];
export type QFastVoterEnrichEntry = QFastVoterEnrichSimple | QFastVoterEnrichNested;

export type QFastSearchPayload = {
    total: QFastTotal;
    results: QFastResults;
    field_options: Record<string, (string | number)[]>;
    voter_enrich?: QFastVoterEnrichEntry[];
};

export type QFastLocsPayload = {
    colors: Record<string, string>;
    points: Array<[string, string, string, number | null, number | null]>;
};

const DEFAULT_TOTAL: QFastTotal = { interviews: 0, voters: 0, famcode: 0 };

const DEFAULT_PAYLOAD: QFastSearchPayload = {
    total: DEFAULT_TOTAL,
    results: {
        aggs: {},
        summary: {},
        timeline: [],
        surveyors: [],
        booth_wise: [],
    },
    field_options: {},
};

export class QFastStore {
    rootStore: any;

    @observable payload: QFastSearchPayload = DEFAULT_PAYLOAD;
    @observable voters: Record<string, unknown>[] = [];
    @observable locs: QFastLocsPayload = { colors: {}, points: [] };
    @observable loading = false;
    @observable votersLoading = false;
    @observable locsLoading = false;
    @observable exporting = false;
    @observable error: string | null = null;
    @observable hasSearchedOnce = false;

    @observable booths: string[] = [];
    @observable filters: Record<string, (string | number)[]> = {};
    @observable timerange: QFastTimerange = "all";
    @observable custom_start_dt: string | null = null;
    @observable custom_end_dt: string | null = null;
    @observable count = 100;
    @observable offset = 0;
    @observable locationOn = false;

    constructor(rootStore: any) {
        makeAutoObservable(this);
        this.rootStore = rootStore;
    }

    private queryBody(includePagination = false): Record<string, unknown> {
        return {
            booths: this.booths.length > 0 ? this.booths : undefined,
            filters:
                Object.keys(this.filters).length > 0 ? this.filters : undefined,
            timerange: this.timerange,
            custom_start_dt: this.custom_start_dt ?? undefined,
            custom_end_dt: this.custom_end_dt ?? undefined,
            ...(includePagination && { count: this.count, offset: this.offset }),
        };
    }

    @action
    setPayload = (data: QFastSearchPayload) => {
        this.payload = data;
        this.error = null;
    };

    @action
    setVoters = (data: Record<string, unknown>[]) => {
        this.voters = data ?? [];
    };

    @action
    setLocs = (data: QFastLocsPayload) => {
        this.locs = data ?? { colors: {}, points: [] };
    };

    @action
    setLoading = (value: boolean) => {
        this.loading = value;
    };

    @action
    setVotersLoading = (value: boolean) => {
        this.votersLoading = value;
    };

    @action
    setLocsLoading = (value: boolean) => {
        this.locsLoading = value;
    };

    @action
    setExporting = (value: boolean) => {
        this.exporting = value;
    };

    @action
    setError = (message: string | null) => {
        this.error = message;
    };

    @action
    setHasSearchedOnce = () => {
        this.hasSearchedOnce = true;
    };

    @action
    setBooths = (booths: string[]) => {
        this.booths = booths;
    };

    @action
    setTimerange = (
        period: QFastTimerange,
        custom_start_dt?: string | null,
        custom_end_dt?: string | null
    ) => {
        this.timerange = period;
        this.custom_start_dt = custom_start_dt ?? null;
        this.custom_end_dt = custom_end_dt ?? null;
    };

    @action
    setDimensionFilter = (fieldKey: string, values: (string | number)[]) => {
        if (values.length === 0) {
            const next = { ...this.filters };
            delete next[fieldKey];
            this.filters = next;
        } else {
            this.filters = { ...this.filters, [fieldKey]: values };
        }
    };

    @action
    setPagination = (count: number, offset: number) => {
        this.count = count;
        this.offset = offset;
    };

    @action
    setLocationOn = (value: boolean) => {
        this.locationOn = value;
    };

    @action
    reset = () => {
        this.payload = DEFAULT_PAYLOAD;
        this.voters = [];
        this.locs = { colors: {}, points: [] };
        this.loading = false;
        this.votersLoading = false;
        this.locsLoading = false;
        this.exporting = false;
        this.locationOn = false;
        this.error = null;
        this.hasSearchedOnce = false;
        this.booths = [];
        this.filters = {};
        this.timerange = "all";
        this.custom_start_dt = null;
        this.custom_end_dt = null;
        this.count = 100;
        this.offset = 0;
    };

    search = async (projectid: string | undefined): Promise<void> => {
        if (!projectid) return;
        this.setLoading(true);
        this.setError(null);
        try {
            const body = { ...this.queryBody(false), projectid: parseInt(projectid, 10) };
            const res = await this.rootStore.api.post("qfast/search", body);
            const data = res.data as QFastSearchPayload;
            this.setPayload(data);
            this.setHasSearchedOnce();
            this.setPagination(this.count, 0);
            await this.loadVoters(projectid);
        } catch (err: any) {
            this.setError(getApiErrorMessage(err, "Search failed"));
        } finally {
            this.setLoading(false);
        }
    };

    loadVoters = async (projectid: string | undefined): Promise<void> => {
        if (!projectid) return;
        this.setVotersLoading(true);
        try {
            const body = { ...this.queryBody(true), projectid: parseInt(projectid, 10) };
            const res = await this.rootStore.api.post("qfast/voters", body);
            this.setVoters((res.data as Record<string, unknown>[]) ?? []);
        } catch (err: any) {
            this.setVoters([]);
            if (!this.error) this.setError(getApiErrorMessage(err, "Failed to load voters"));
        } finally {
            this.setVotersLoading(false);
        }
    };

    loadLocs = async (projectid: string | undefined): Promise<void> => {
        if (!projectid) return;
        this.setLocsLoading(true);
        try {
            const body = { ...this.queryBody(false), projectid: parseInt(projectid, 10) };
            const res = await this.rootStore.api.post("qfast/locs", body);
            this.setLocs((res.data as QFastLocsPayload) ?? { colors: {}, points: [] });
        } catch (err: any) {
            this.setLocs({ colors: {}, points: [] });
        } finally {
            this.setLocsLoading(false);
        }
    };

    /** Export voters as CSV via backend (same search params, max 5000). Triggers file download. */
    exportVoters = async (
        projectid: string | undefined,
        projectName: string
    ): Promise<void> => {
        if (!projectid) return;
        if (this.exporting) return;
        this.setExporting(true);
        try {
            const body = {
                ...this.queryBody(false),
                projectid: parseInt(projectid, 10),
                project_name: projectName || undefined,
            };
            const res = await this.rootStore.api.post("qfast/export", body, {
                responseType: "blob",
            });
            if (res.status && res.status >= 400) {
                const msg = await blobToErrorMessage(res.data as Blob);
                this.setError(msg);
                return;
            }
            const blob = res.data as Blob;
            const filename =
                parseFilenameFromContentDisposition(
                    res.headers?.["content-disposition"] ?? res.headers?.["Content-Disposition"]
                ) ||
                `${(projectName || "export").replace(/[^\w\s-]/g, "").trim() || "export"}_export_${new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19)}.csv`;
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err: any) {
            this.setError(getApiErrorMessage(err, "Export failed"));
        } finally {
            this.setExporting(false);
        }
    };
}

const MAX_ERROR_LENGTH = 280;

function truncateMessage(s: string): string {
    if (s.length <= MAX_ERROR_LENGTH) return s;
    return s.slice(0, MAX_ERROR_LENGTH).trim() + "…";
}

/** Extract a short user-facing message from an API/axios error (avoids "catastrophic" raw errors). */
function getApiErrorMessage(err: any, fallback: string): string {
    if (!err) return fallback;
    const data = err.response?.data;
    if (data != null && typeof data === "object") {
        const d = data.detail;
        if (typeof d === "string") return truncateMessage(d);
        if (Array.isArray(d) && d.length > 0) {
            const first = d[0];
            if (first?.msg != null) return truncateMessage(String(first.msg));
            if (typeof first === "string") return truncateMessage(first);
        }
        if (typeof data.message === "string") return truncateMessage(data.message);
    }
    if (typeof data === "string" && data.length > 0) return truncateMessage(data);
    const msg = err?.message;
    if (typeof msg === "string" && msg.length > 0) {
        if (msg.includes("status code")) return `${fallback}. Please try again.`;
        return msg.length < 300 ? msg : fallback;
    }
    return fallback;
}

function parseFilenameFromContentDisposition(header: string | undefined): string | null {
    if (!header) return null;
    const m = header.match(/filename\*?=(?:UTF-8'')?["']?([^"';\n]+)["']?/i) ||
        header.match(/filename=["']?([^"';\n]+)["']?/i);
    return m ? m[1].trim() : null;
}

async function blobToErrorMessage(blob: Blob): Promise<string> {
    try {
        const text = await blob.text();
        const j = JSON.parse(text);
        return (j.detail ?? j.message ?? text) || "Export failed";
    } catch {
        return "Export failed";
    }
}
